import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Typography,
} from '@mui/material';
import { ItemCotizacionData, OfertaData, CotizacionData, ItemOfertaData } from '../types';
import { usePriceCalculation } from './ItemForm/usePriceCalculation';
import { useLocalDate } from './ItemForm/useLocalDate';
import { ItemFormHeader } from './ItemForm/ItemFormHeader';
import { ItemFormFields } from './ItemForm/ItemFormFields';

interface ItemFormProps {
  item: ItemCotizacionData;
  itemIndex: number;
  dataOferta: OfertaData;
  setDataOferta: React.Dispatch<React.SetStateAction<OfertaData>>;
  parentData: CotizacionData; // Para validaciones
  shouldValidate?: boolean; // Si debe mostrar validaciones de campos requeridos
  forceExpanded?: boolean;
}

const ItemForm: React.FC<ItemFormProps> = ({
  item,
  itemIndex,
  dataOferta,
  setDataOferta,
  parentData,
  shouldValidate = true,
  forceExpanded = false,
}) => {
  // Verificar que el item existe antes de acceder a sus propiedades
  const currentItem = dataOferta.items_oferta?.[itemIndex];

  // Estado para controlar si el formulario está colapsado
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (forceExpanded) {
      setIsCollapsed(false);
    }
  }, [forceExpanded]);

  // Hook para manejar el estado local de la fecha "Válido Hasta"
  const { localSelectedDate, setLocalSelectedDate } = useLocalDate(
    currentItem?.valido_hasta,
  );

  // Hook para calcular precios automáticamente
  usePriceCalculation({
    currentItem,
    itemIndex,
    setDataOferta,
  });

  // Función para manejar cambios en los campos del item
  const handleItemChange = useCallback(
    (field: keyof ItemOfertaData, value: string | number) => {
      if (!currentItem) return;
      setDataOferta((prev) => ({
        ...prev,
        items_oferta: prev.items_oferta.map((item, index) =>
          index === itemIndex ? { ...item, [field]: value } : item,
        ),
      }));
    },
    [itemIndex, setDataOferta, currentItem],
  );

  // Si no hay item, mostrar mensaje de error
  if (!currentItem) {
    return (
      <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography color="error">
          Error: Item no encontrado en la posición {itemIndex}
        </Typography>
      </Box>
    );
  }

  // Si no hay item, no renderizar nada
  if (!item || !currentItem) {
    return null;
  }

  return (
    <Box
      id={`item-form-${itemIndex}`}
      sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}
    >
      {/* Header del Item con botón de colapsar */}
      <ItemFormHeader
        item={item}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {/* Contenido colapsable */}
      <Collapse in={!isCollapsed}>
        {/* Grid de campos */}
        <ItemFormFields
          currentItem={currentItem}
          item={item}
          itemIndex={itemIndex}
          parentData={parentData}
          localSelectedDate={localSelectedDate}
          setLocalSelectedDate={setLocalSelectedDate}
          handleItemChange={handleItemChange}
          shouldValidate={shouldValidate}
        />
      </Collapse>
    </Box>
  );
};

export default ItemForm;

